import type { PropertyContactWithDetails, PropertyContactsSummary } from '@/client';
import { findContactByEmail } from '@/utils/access/sharingAccessUtils';

/**
 * `scope` is the one field the payload carries that the generated contract does not.
 *
 * It is candidate context and does not survive confirmation, so a saved contact has none — but the
 * synthesised record behind an unreviewed provider does, and the views read it from here. Keeping
 * the extension named stops the field being smuggled through structural typing.
 */
export type PropertyContactWithServiceFields = PropertyContactWithDetails & {
	scope?: string | null;
};

/**
 * What to show for a contact: the household's correction where it made one, the registry's value
 * otherwise.
 *
 * A resolve at the point of display, never a value to write back. `professional_contacts` keeps the
 * pipeline's value underneath, which is what makes "documents say…" answerable at all — collapsing
 * the two here would throw that away.
 */
/**
 * A card named only by its own address has no name yet: an invite with no name typed files the
 * address in its place. The account it names may have one, so that is shown instead. Read only;
 * the card keeps what it holds, and a name anyone typed on it still wins.
 */
const displayName = (contact: PropertyContactWithDetails): string => {
	const name = contact.contact.name;
	const email = contact.contact.email?.trim().toLowerCase();
	const accountName = contact.account?.name?.trim();
	return accountName && email && name.trim().toLowerCase() === email ? accountName : name;
};

/**
 * The account a card names, wherever the response put it: the linked account, the card's own
 * column, or the registry contact's.
 */
export const accountUserIdOf = (contact: PropertyContactWithDetails): number | null | undefined =>
	contact.account?.user_id ?? contact.account_user_id ?? contact.contact.user_id;

export const resolvedContactFields = (contact: PropertyContactWithDetails) => ({
	name: contact.override_name ?? displayName(contact),
	company: contact.override_company ?? contact.contact.company ?? null,
	email: contact.override_email ?? contact.contact.email ?? null,
	phone: contact.override_phone ?? contact.contact.phone ?? null,
});

/**
 * The other cards at the same company that still hold access.
 *
 * Disconnecting acts on one card, so a company connected through a person keeps its access when the
 * company's own card is disconnected, and the other way round. True, and invisible — which reads as
 * the button having failed unless the page says who is left.
 *
 * Matched on `contact.company` as written, which is the only link between the two today. A
 * `company_id` is coming; until it does, two spellings of one company read as two companies, so
 * this can only under-report.
 */
export const companyCardsWithAccess = (
	contact: PropertyContactWithDetails,
	allContacts: readonly PropertyContactWithDetails[],
): PropertyContactWithDetails[] => {
	const company = contact.contact.company?.trim().toLowerCase();
	if (!company) return [];

	return allContacts.filter(
		(other) =>
			other.id !== contact.id &&
			other.is_connected &&
			// A card on the same access row is not one that keeps it — see `cardsSharingAccount`.
			accountUserIdOf(other) !== accountUserIdOf(contact) &&
			other.contact.company?.trim().toLowerCase() === company,
	);
};

/**
 * The other cards that hold access through the same account, and so go with this one.
 *
 * Access is the account's, not the card's: two cards naming one account — the same person filed
 * under two trades, or two registry rows for one human — share a single access row. Disconnecting
 * from either takes it from both, and the second card then refuses a disconnect of its own, so the
 * press has to say which cards it is about before it happens.
 */
export const cardsSharingAccount = (
	contact: PropertyContactWithDetails,
	allContacts: readonly PropertyContactWithDetails[],
): PropertyContactWithDetails[] => {
	const accountUserId = accountUserIdOf(contact);
	if (accountUserId == null) return [];

	return allContacts.filter(
		(other) => other.id !== contact.id && other.is_connected && accountUserIdOf(other) === accountUserId,
	);
};

/**
 * Why an address cannot go on this card: another card on the property already holds it, and a
 * second would be the same person filed twice. `exceptId` is the card being edited, which may keep
 * its own. Null when the address is free.
 *
 * TODO(services/one-card-per-email): The API does not enforce this, so two tabs or two people can
 * still file one address twice. Enforce a normalised (trimmed, lower-case) email unique per property
 * there, returning 409 as a duplicate role does, and keep this check as the early message.
 */
export const emailHeldByAnotherCard = (
	contacts: PropertyContactsSummary | undefined,
	email: string,
	exceptId?: number,
): string | null => {
	const other = findContactByEmail(contacts, email, exceptId);
	if (!other) return null;
	const { name } = resolvedContactFields(other);
	return other.historical ? `Already saved as ${name}, in History` : `Already saved as ${name}`;
};
